import axios, { type AxiosRequestConfig, AxiosError } from 'axios';
import i18n from '../../language/i18n';

// Extended config to allow per-request token overrides
interface HttpClientConfig extends AxiosRequestConfig {
  accessToken?: string | null;
  _retry?: boolean;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 100000,
});

// -------------------- REQUEST INTERCEPTOR --------------------
api.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const stored = localStorage.getItem('insurance-auth-storage');
      let rawToken: string | null = null;

      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          rawToken = parsed?.state?.token ?? null;
        } catch (e) {
          console.error('Failed to parse token from localStorage:', e);
        }
      }

      if (rawToken) {
        config.headers.Authorization = `Bearer ${rawToken}`;
      }
    config.headers['Accept-Language'] = i18n.language;
    }
    return config;
  },
  (error) => {
    console.error('[httpClient] Request error:', error);
    return Promise.reject(error);
  }
);

// -------------------- REFRESH TOKEN LOGIC --------------------
let isRefreshing = false;
let failedQueue: {
  resolve: (value: string | null | PromiseLike<string | null>) => void;
  reject: (reason?: unknown) => void;
}[] = [];

const processQueue = (error: unknown | null = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const refreshAuthToken = async (): Promise<string | null> => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const stored = localStorage.getItem('insurance-auth-storage');
    let refreshToken: string | null = null;

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        refreshToken = parsed?.state?.refreshToken ?? null;
      } catch (e) {
        console.error('Failed to parse refresh token:', e);
      }
    }

    if (!refreshToken) throw new Error('No refresh token found.');

    // 🚫 use plain axios (not api) to avoid interceptor loop
    const response = await axios.post<{ accessToken: string; refreshToken: string }>(
      `${import.meta.env.VITE_API_BASE_URL}/Auth/refresh-token`,
      { refreshToken }
    );

    const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

    // ✅ Save tokens consistently
    const currentAuthStorage = JSON.parse(localStorage.getItem('insurance-auth-storage') || '{}');
    localStorage.setItem(
      'insurance-auth-storage',
      JSON.stringify({
        ...currentAuthStorage,
        state: {
          ...currentAuthStorage.state,
          token: newAccessToken,
          refreshToken: newRefreshToken,
        },
      })
    );

    isRefreshing = false;
    processQueue(null, newAccessToken);
    return newAccessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    isRefreshing = false;
    processQueue(error, null);

    localStorage.removeItem('insurance-auth-storage');
    window.location.href = '/auth/login';
    return null;
  }
};

// -------------------- RESPONSE INTERCEPTOR --------------------
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as HttpClientConfig;

    if (
        error.response?.status === 401  &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/Auth/refresh-token')
    ) {
      console.log('[httpClient] Intercepting 401 Error');
      originalRequest._retry = true;
      try {
        console.log('[httpClient] Attempting to refresh token');
        const newAccessToken = await refreshAuthToken();
        if (newAccessToken) {
          console.log('[httpClient] Token refreshed successfully. Retrying original request.');
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };
          return api(originalRequest); // retry request
        }
      } catch (refreshError) {
        console.error('[httpClient] Error refreshing token:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// -------------------- HTTP HELPERS --------------------
const get = async <T>(url: string, config: HttpClientConfig = {}): Promise<T> => {
  if (config.accessToken) {
    config.headers = { ...config.headers, Authorization: `Bearer ${config.accessToken}` };
  }
  const response = await api.get<T>(url, config);
  return response.data;
};

const post = async <T>(url: string, data: unknown, config: HttpClientConfig = {}): Promise<T> => {
  const mergedConfig: HttpClientConfig = {
    ...config,
    headers: { 'Content-Type': 'application/json', ...config.headers },
  };

  if (data instanceof FormData && mergedConfig.headers) {
    delete mergedConfig.headers['Content-Type'];
  }

  if (mergedConfig.accessToken) {
    mergedConfig.headers = {
      ...mergedConfig.headers,
      Authorization: `Bearer ${mergedConfig.accessToken}`,
    };
  }

  const response = await api.post<T>(url, data, mergedConfig);
  return response.data;
};

const put = async <T>(url: string, data: unknown, config: HttpClientConfig = {}): Promise<T> => {
  config.headers = { 'Content-Type': 'application/json', ...config.headers };
  if (config.accessToken) {
    config.headers.Authorization = `Bearer ${config.accessToken}`;
  }
  const response = await api.put<T>(url, data, config);
  return response.data;
};

const del = async <T>(url: string, config: HttpClientConfig = {}): Promise<T> => {
  if (config.accessToken) {
    config.headers = { ...config.headers, Authorization: `Bearer ${config.accessToken}` };
  }
  const response = await api.delete<T>(url, config);
  return response.data;
};

export { get, post, put, del };